import { useConfirm } from "@/components/DialogProvider";
import { StatusChangePopover } from "@/components/projects/StatusChangePopover";
import { MultiSelectCombobox } from "@/components/shadcn-custom/MultiSelectCombobox";
import { Checkbox } from "@/components/shadcn-custom/ShadcnCheckbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/shadcn-custom/ShadcnPagination";
import { StatusBadge } from "@/components/shadcn-custom/ShadcnStatus";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";
import { useConfig, useProjectStatuses } from "@/features/config/hooks/useConfig";
import { useProjects } from "@/features/projects/hooks/useProjects";
import { useUsers } from "@/features/users/hooks/useUsers";
import { getProjectWorkUnitStats } from "@/features/work-units/api/workUnitHelpers";
import { db } from "@/firebase";
import { isWorkerAssignedToProject } from "@/firestore_types";
import useLocalStorage from "@/hooks/useLocalStorage";
import { getProjectStatusAlerts } from "@/lib/projectStatusAlerts";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { doc, Timestamp, updateDoc } from "firebase/firestore";
import { Building2, Calendar, Filter, MapPin, Plus, Search, User } from "lucide-react";
import {
  parseAsArrayOf,
  parseAsBoolean,
  parseAsString,
  parseAsStringEnum,
  useQueryStates,
} from "nuqs";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useAdminAgencies, useAdminProjects, useAdminWorkers } from "../-admin-context";
import { ProjectsCalendarView } from "./-projects-calendar-view";
import { ProjectsMapView } from "./-projects-map-view";

/**
 * Converts a Date object to a date string (YYYY-MM-DD).
 * The calendar picker returns a Date object at midnight local time representing the user's visual selection.
 * We extract the date components directly to preserve the selected date without timezone shifting.
 */
const dateToDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Converts a date string (YYYY-MM-DD) to a Date object at local midnight.
 * This prevents timezone shifting issues when displaying dates in the calendar picker.
 */
const dateStringToDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day);
};

export const Route = createFileRoute("/dashboard/admin/projects/")({
  component: RouteComponent,
});

type ViewMode = "list" | "grid" | "map" | "calendar";
type TabFilter = "all" | "ongoing" | "onetime";
type SortField =
  | "name"
  | "customer"
  | "type"
  | "status"
  | "manager"
  | "alert"
  | "agency"
  | "workers";
type SortDirection = "asc" | "desc";
interface FilterState {
  customers: string[]; // customer UIDs
  types: string[]; // "Ongoing" or "One-time"
  statuses: string[]; // status IDs
  managers: string[]; // manager UIDs
  agencies: string[]; // agency IDs
  workers: string[]; // worker IDs
  projectStartFrom: string; // Filter: project start date from (YYYY-MM-DD)
  projectStartTo: string; // Filter: project start date to (YYYY-MM-DD)
  projectEndFrom: string; // Filter: project end date from (YYYY-MM-DD)
  projectEndTo: string; // Filter: project end date to (YYYY-MM-DD)
  admin: string[]; // admin UIDs
  showOnlyWithAlert: boolean;
  showHidden: boolean;
  workerStatus: string[]; // "fully_staffed" | "understaffed" | "overstaffed" | "no_work_units"
}

const EmptyProjectsState = () => (
  <div className="flex min-h-[calc(100vh-500px)] flex-col items-center justify-center">
    <svg
      width="160"
      height="80"
      viewBox="0 0 160 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M66.5 16C64.0147 16 62 18.0147 62 20.5C62 22.9853 64.0147 25 66.5 25H40.5C38.0147 25 36 27.0147 36 29.5C36 31.9853 38.0147 34 40.5 34H56.5C58.9853 34 61 36.0147 61 38.5C61 40.9853 58.9853 43 56.5 43H32.5C30.0147 43 28 45.0147 28 47.5C28 49.9853 30.0147 52 32.5 52H56.5C54.0147 52 52 54.0147 52 56.5C52 58.9853 54.0147 61 56.5 61H112.5C114.985 61 117 58.9853 117 56.5C117 54.0147 114.985 52 112.5 52H111.25C108.765 52 106.75 49.9853 106.75 47.5C106.75 45.0147 108.765 43 111.25 43H117.5C119.985 43 122 40.9853 122 38.5C122 36.0147 119.985 34 117.5 34H103.5C105.985 34 108 31.9853 108 29.5C108 27.0147 105.985 25 103.5 25H128.5C130.985 25 133 22.9853 133 20.5C133 18.0147 130.985 16 128.5 16H66.5Z"
        fill="#EDE9FE"
      />
      <path
        d="M128.5 43C130.985 43 133 40.9853 133 38.5C133 36.0147 130.985 34 128.5 34C126.015 34 124 36.0147 124 38.5C124 40.9853 126.015 43 128.5 43Z"
        fill="#EDE9FE"
      />
      <path
        d="M50.1855 20.75H66.5684C67.9292 20.75 69.032 21.8531 69.0322 23.2139C69.0322 25.4033 70.8077 27.1787 72.9971 27.1787H99.8145C102.264 27.1787 104.25 29.164 104.25 31.6133V68.8145C104.25 71.2639 102.264 73.25 99.8145 73.25H50.1855C47.7361 73.25 45.75 71.2639 45.75 68.8145V25.1855C45.75 22.7361 47.7361 20.75 50.1855 20.75Z"
        fill="white"
        stroke="#7F22FE"
        strokeWidth="1.5"
      />
      <path
        d="M65 27.5926C65 26.1607 63.8393 25 62.4074 25H52.5926C51.1607 25 50 26.1607 50 27.5926V61H100V33.5926C100 32.1607 98.8393 31 97.4074 31H67.5926C66.1607 31 65 29.8393 65 28.4074V27.5926Z"
        fill="#EDE9FE"
      />
      <path
        d="M60.6857 34.7247H109.944C112.379 34.7247 113.854 36.6219 113.247 38.9746L105.527 68.893C104.913 71.2735 102.404 73.2144 99.9402 73.2146H50.6805C48.2457 73.2144 46.7722 71.3173 47.3792 68.9647L55.0987 39.0463C55.7129 36.6657 58.2217 34.7248 60.6857 34.7247Z"
        fill="white"
        stroke="#7F22FE"
        strokeWidth="1.5"
      />
      <path
        d="M101 66.9328C83.9415 64.4612 70.9507 53.175 64.8947 38L63.2075 38C61.7636 37.9982 60.2875 39.087 59.8968 40.4419L52.0958 67.4943C51.6983 68.8729 52.5747 70 54.0442 70L97.5201 69.9999C98.9752 69.9999 100.459 68.8941 100.836 67.529L101 66.9328Z"
        fill="#EDE9FE"
      />
      <circle cx="75.8091" cy="7.24074" r="2.54074" stroke="#8E51FF" strokeWidth="1.4" />
      <path
        d="M80.8358 14.8615L86.3355 20.3612M86.3355 14.8615L80.8358 20.3612"
        stroke="#8E51FF"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <circle cx="97.2668" cy="17.0666" r="2.8" fill="#8E51FF" />
    </svg>
    <h3 className="mt-4 text-lg font-semibold text-gray-900">Projects is empty</h3>
    <p className="mt-1 text-sm text-gray-600">Wait until there&apos;s at least one project</p>
    <Link to="/dashboard/admin/projects/create">
      <Button variant="primary" size="default" className="mt-4">
        <Plus className="mr-2 h-4 w-4" />
        Create Project
      </Button>
    </Link>
  </div>
);

const ProjectsGridShimmer = () => (
  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
    {Array.from({ length: 8 }).map((_, i) => (
      <div key={i} className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-3 flex items-start justify-between">
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="h-8 w-8 rounded-full" />
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-4" />
            <Skeleton className="h-4 w-2/3" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-4" />
            <Skeleton className="h-4 w-3/4" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-4" />
            <Skeleton className="h-4 w-1/3" />
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between">
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-6 w-16" />
        </div>
      </div>
    ))}
  </div>
);

const ProjectsListShimmer = () => (
  <div className="rounded-lg border border-gray-200 bg-white">
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Project Name</TableHead>
          <TableHead>Customer</TableHead>
          <TableHead>Agency</TableHead>
          <TableHead>Managers</TableHead>
          <TableHead>Workers</TableHead>
          <TableHead>Start Date</TableHead>
          <TableHead />
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: 10 }).map((_, i) => (
          <TableRow key={i}>
            <TableCell>
              <Skeleton className="h-5 w-48" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-5 w-32" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-5 w-24" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-5 w-40" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-5 w-16" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-5 w-24" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-8 w-8 rounded-full" />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

function RouteComponent() {
  // Fetch real data
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const projectResource = useAdminProjects();
  const agenciesResource = useAdminAgencies();
  const { users } = useUsers();
  useConfig();
  const { projectStatuses } = useProjectStatuses();
  const { deleting, deleteProject } = useProjects();
  const confirm = useConfirm();
  const realProjects = useMemo(() => projectResource.asData || [], [projectResource.asData]);
  const agencies = useMemo(() => agenciesResource.asData || [], [agenciesResource.asData]);
  const workersResource = useAdminWorkers();
  const workers = workersResource.asData || [];

  // Use nuqs for URL state management
  const [urlState, setUrlState] = useQueryStates(
    {
      page: parseAsString.withDefault("1"),
      search: parseAsString.withDefault(""),
      tab: parseAsStringEnum<TabFilter>(["all", "ongoing", "onetime"]).withDefault("all"),
      customers: parseAsArrayOf(parseAsString).withDefault([]),
      types: parseAsArrayOf(parseAsString).withDefault([]),
      statuses: parseAsArrayOf(parseAsString).withDefault([]),
      managers: parseAsArrayOf(parseAsString).withDefault([]),
      agencies: parseAsArrayOf(parseAsString).withDefault([]),
      workers: parseAsArrayOf(parseAsString).withDefault([]),
      projectStartFrom: parseAsString.withDefault(""),
      projectStartTo: parseAsString.withDefault(""),
      projectEndFrom: parseAsString.withDefault(""),
      projectEndTo: parseAsString.withDefault(""),
      admin: parseAsArrayOf(parseAsString).withDefault([]),
      showOnlyWithAlert: parseAsBoolean.withDefault(false),
      showHidden: parseAsBoolean.withDefault(false),
      workerStatus: parseAsArrayOf(parseAsString).withDefault([]),
      sortField: parseAsStringEnum<SortField>([
        "name",
        "customer",
        "type",
        "status",
        "manager",
        "alert",
        "agency",
        "workers",
      ]).withDefault("name"),
      sortDirection: parseAsStringEnum<SortDirection>(["asc", "desc"]).withDefault("asc"),
    },
    { history: "replace" },
  );

  const currentPage = parseInt(urlState.page);
  const activeTab = urlState.tab;

  // Local search state drives filtering instantly; URL is updated lazily
  const [localSearch, setLocalSearch] = useState(urlState.search);
  const urlSearchDebounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local state if URL search changes externally (e.g. back/forward navigation)
  // We compare against trimmed localSearch because the URL serializer strips trailing
  // whitespace, so a round-trip echo of the user's own typing must not overwrite
  // the local value (which may legitimately contain a trailing space mid-typing).
  const prevUrlSearch = useRef(urlState.search);
  useEffect(() => {
    if (urlState.search !== prevUrlSearch.current) {
      prevUrlSearch.current = urlState.search;
      // Only overwrite local state when the URL changed to something genuinely
      // different from what the user has typed (e.g. back/forward navigation).
      // If the URL value merely equals the trimmed version of what the user is
      // currently typing, leave localSearch alone so trailing spaces are preserved.
      setLocalSearch((current) => {
        if (current.trim() === urlState.search) return current;
        return urlState.search;
      });
    }
  }, [urlState.search]);

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (urlSearchDebounceTimer.current) clearTimeout(urlSearchDebounceTimer.current);
    };
  }, []);

  const searchQuery = localSearch;
  const sortField = urlState.sortField;
  const sortDirection = urlState.sortDirection;

  const filters: FilterState = useMemo(
    () => ({
      customers: urlState.customers,
      types: urlState.types,
      statuses: urlState.statuses,
      managers: urlState.managers,
      agencies: urlState.agencies,
      workers: urlState.workers,
      projectStartFrom: urlState.projectStartFrom,
      projectStartTo: urlState.projectStartTo,
      projectEndFrom: urlState.projectEndFrom,
      projectEndTo: urlState.projectEndTo,
      admin: urlState.admin,
      showOnlyWithAlert: urlState.showOnlyWithAlert,
      showHidden: urlState.showHidden,
      workerStatus: urlState.workerStatus,
    }),
    [
      urlState.customers,
      urlState.types,
      urlState.statuses,
      urlState.managers,
      urlState.agencies,
      urlState.workers,
      urlState.projectStartFrom,
      urlState.projectStartTo,
      urlState.projectEndFrom,
      urlState.projectEndTo,
      urlState.admin,
      urlState.showOnlyWithAlert,
      urlState.showHidden,
      urlState.workerStatus,
    ],
  );

  const [viewMode, setViewMode] = useLocalStorage<ViewMode>("project-view-mode", "grid");
  const [filterOpen, setFilterOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState<FilterState>(filters);
  const [visibleColumns, setVisibleColumns] = useState({
    customer: true,
    agency: true,
    manager: true,
    workers: true,
    startDate: true,
  });

  // Popover states for date range pickers
  const [isStartDatePopoverOpen, setIsStartDatePopoverOpen] = useState(false);
  const [isEndDatePopoverOpen, setIsEndDatePopoverOpen] = useState(false);

  // Convert URL date strings to Date objects for calendar pickers
  const projectStartFromDate = tempFilters.projectStartFrom
    ? dateStringToDate(tempFilters.projectStartFrom)
    : undefined;
  const projectStartToDate = tempFilters.projectStartTo
    ? dateStringToDate(tempFilters.projectStartTo)
    : undefined;
  const projectEndFromDate = tempFilters.projectEndFrom
    ? dateStringToDate(tempFilters.projectEndFrom)
    : undefined;
  const projectEndToDate = tempFilters.projectEndTo
    ? dateStringToDate(tempFilters.projectEndTo)
    : undefined;

  // Sync tempFilters with URL filters when they change
  useEffect(() => {
    setTempFilters(filters);
  }, [filters]);

  const toggleColumn = (column: keyof typeof visibleColumns) => {
    setVisibleColumns((prev) => ({ ...prev, [column]: !prev[column] }));
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setUrlState({ sortDirection: sortDirection === "asc" ? "desc" : "asc" });
    } else {
      // Set new field with ascending direction
      setUrlState({ sortField: field, sortDirection: "asc" });
    }
  };

  const hasActiveFilters = () => {
    return (
      filters.customers.length > 0 ||
      filters.types.length > 0 ||
      filters.statuses.length > 0 ||
      filters.managers.length > 0 ||
      filters.agencies.length > 0 ||
      filters.workers.length > 0 ||
      filters.projectStartFrom !== "" ||
      filters.projectStartTo !== "" ||
      filters.projectEndFrom !== "" ||
      filters.projectEndTo !== "" ||
      filters.admin.length > 0 ||
      filters.showOnlyWithAlert ||
      filters.showHidden ||
      filters.workerStatus.length > 0
    );
  };

  const applyFilters = () => {
    setUrlState({
      customers: tempFilters.customers,
      types: tempFilters.types,
      statuses: tempFilters.statuses,
      managers: tempFilters.managers,
      agencies: tempFilters.agencies,
      workers: tempFilters.workers,
      projectStartFrom: tempFilters.projectStartFrom,
      projectStartTo: tempFilters.projectStartTo,
      projectEndFrom: tempFilters.projectEndFrom,
      projectEndTo: tempFilters.projectEndTo,
      admin: tempFilters.admin,
      showOnlyWithAlert: tempFilters.showOnlyWithAlert,
      showHidden: tempFilters.showHidden,
      workerStatus: tempFilters.workerStatus,
      page: "1",
    });
    setFilterOpen(false);
  };

  const resetFilters = () => {
    const emptyFilters: FilterState = {
      customers: [],
      types: [],
      statuses: [],
      managers: [],
      agencies: [],
      workers: [],
      projectStartFrom: "",
      projectStartTo: "",
      projectEndFrom: "",
      projectEndTo: "",
      admin: [],
      showOnlyWithAlert: false,
      showHidden: false,
      workerStatus: [],
    };
    setTempFilters(emptyFilters);
    setUrlState({
      customers: [],
      types: [],
      statuses: [],
      managers: [],
      agencies: [],
      workers: [],
      projectStartFrom: "",
      projectStartTo: "",
      projectEndFrom: "",
      projectEndTo: "",
      admin: [],
      showOnlyWithAlert: false,
      showHidden: false,
      workerStatus: [],
      page: "1",
    });
  };

  // Handle delete project (soft delete)
  const handleDeleteProject = async (id: string) => {
    const confirmation = await confirm({
      title: "Are you sure you want to delete this project?",
      description: "This will delete the project and all associated data.",
      confirmText: "Delete Project",
      cancelText: "Cancel",
    });
    if (!confirmation) {
      return;
    }
    try {
      await deleteProject(id);
    } catch (err) {
      console.error("Failed to delete project:", err);
    }
  };

  // Handle status change
  const handleStatusChange = async (projectId: string, newStatusId: string) => {
    if (!userProfile?.uid) {
      toast.error("You must be logged in to change project status");
      return;
    }

    try {
      const projectRef = doc(db, "projects", projectId);
      await updateDoc(projectRef, {
        statusId: newStatusId,
        updatedAt: Timestamp.now(),
        updatedBy: userProfile.uid,
      });

      const newStatus = projectStatuses.find((s) => s.id === newStatusId);
      toast.success(`Project status updated to ${newStatus?.name || "new status"}`);
    } catch (error) {
      console.error("Error updating project status:", error);
      toast.error("Failed to update project status. Please try again.");
      throw error;
    }
  };

  // Transform real projects to UI format
  const projects = useMemo(() => {
    const realProjectsMapped = realProjects.map((project) => {
      const customer = users.find((u) => u.uid === project.customerId);
      const managerIds = project.assignedManagerIds || [];
      const managers = managerIds
        .map((id) => users.find((u) => u.uid === id))
        .filter((m) => m !== undefined);
      const managerNames = managers.length > 0 ? managers.map((m) => m.name).join(", ") : "-";
      const agency = agencies.find((a) => a.id === project.assignedAgencyId);
      const workUnitStats = getProjectWorkUnitStats(project.workUnits || []);
      const alerts = getProjectStatusAlerts([project], projectStatuses);
      const statusConfig = project.statusId
        ? projectStatuses.find((s) => s.id === project.statusId)
        : null;

      return {
        id: project.id,
        name: project.name || "Untitled Project",
        customer: customer?.name || "-",
        customerId: project.customerId || "",
        type: project.type === "one-time" ? "One-time" : "Ongoing",
        status: statusConfig?.name || "",
        statusColor: statusConfig?.color || "",
        managers: managerNames,
        managerIds: managerIds,
        alert: alerts.length > 0 ? alerts[0].statusName : "-",
        alertDays: alerts.length > 0 ? `${alerts[0].daysSinceChange} days over threshold` : "",
        agency: agency?.name || "-",
        agencyId: project.assignedAgencyId || "",
        workers: `${workUnitStats.totalActiveWorkers}/${workUnitStats.totalWorkersNeeded}`,
        location: project.jobLocation
          ? typeof project.jobLocation === "string"
            ? project.jobLocation
            : project.jobLocation.address || "-"
          : "-",
        startDate: project.startDate || null,
        endDate: project.endDate || null,
      };
    });

    return realProjectsMapped;
  }, [realProjects, users, agencies, projectStatuses]);

  // Filter and sort projects
  const filteredProjects = useMemo(() => {
    let filtered = projects.filter((project) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          project.name.toLowerCase().includes(query) ||
          project.customer.toLowerCase().includes(query) ||
          project.managers.toLowerCase().includes(query) ||
          project.location.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Tab filter
      if (activeTab === "ongoing" && project.type !== "Ongoing") return false;
      if (activeTab === "onetime" && project.type !== "One-time") return false;

      // Apply all filters (using IDs)
      if (filters.customers.length > 0 && !filters.customers.includes(project.customerId))
        return false;
      if (filters.types.length > 0 && !filters.types.includes(project.type)) return false;
      if (filters.statuses.length > 0) {
        const realProject = realProjects.find((rp) => rp.id === project.id);
        if (!realProject || !filters.statuses.includes(realProject.statusId || "")) return false;
      }
      if (
        filters.managers.length > 0 &&
        !project.managerIds.some((id) => filters.managers.includes(id))
      )
        return false;
      if (filters.agencies.length > 0 && !filters.agencies.includes(project.agencyId)) return false;

      // Workers filter - check if any selected worker is assigned to the project
      if (filters.workers.length > 0) {
        const realProject = realProjects.find((rp) => rp.id === project.id);
        if (!realProject) return false;
        const hasMatchingWorker = filters.workers.some((workerId) =>
          isWorkerAssignedToProject(realProject, workerId),
        );
        if (!hasMatchingWorker) return false;
      }

      // Admin filter - check if project's assignedAdminId matches any selected admin
      // Also exclude projects whose admin doesn't exist in users
      if (filters.admin.length > 0) {
        const realProject = realProjects.find((rp) => rp.id === project.id);
        if (!realProject || !realProject.assignedAdminId) return false;
        // Check if the admin exists in users
        const adminExists = users.find((u) => u.uid === realProject.assignedAdminId);
        if (!adminExists) return false;
        if (!filters.admin.includes(realProject.assignedAdminId)) return false;
      }

      if (filters.showOnlyWithAlert && project.alert === "-") return false;

      // Filter out hidden statuses unless showHidden is enabled
      if (!filters.showHidden) {
        const realProject = realProjects.find((rp) => rp.id === project.id);
        if (realProject && realProject.statusId) {
          const statusConfig = projectStatuses.find((s) => s.id === realProject.statusId);
          if (statusConfig?.hideOnProjectList) return false;
        }
      }

      // Date filters - Project Start Date Range
      if (filters.projectStartFrom) {
        const filterStartFrom = new Date(filters.projectStartFrom);
        const projectStartDate = project.startDate ? project.startDate.toDate() : null;
        if (!projectStartDate || projectStartDate < filterStartFrom) return false;
      }
      if (filters.projectStartTo) {
        const filterStartTo = new Date(filters.projectStartTo);
        const projectStartDate = project.startDate ? project.startDate.toDate() : null;
        if (!projectStartDate || projectStartDate > filterStartTo) return false;
      }

      // Date filters - Project End Date Range
      if (filters.projectEndFrom) {
        const filterEndFrom = new Date(filters.projectEndFrom);
        const projectEndDate = project.endDate ? project.endDate.toDate() : null;
        if (!projectEndDate || projectEndDate < filterEndFrom) return false;
      }
      if (filters.projectEndTo) {
        const filterEndTo = new Date(filters.projectEndTo);
        const projectEndDate = project.endDate ? project.endDate.toDate() : null;
        if (!projectEndDate || projectEndDate > filterEndTo) return false;
      }

      // Worker Status filter
      if (filters.workerStatus.length > 0) {
        // Note: This find() call has O(n) complexity per project.
        // For large datasets, consider optimizing with a Map lookup.
        const realProject = realProjects.find((rp) => rp.id === project.id);
        if (!realProject) return false;

        const stats = getProjectWorkUnitStats(realProject.workUnits);

        let projectWorkerStatus: string;
        if (stats.totalWorkUnits === 0) {
          projectWorkerStatus = "no_work_units";
        } else if (stats.totalActiveWorkers === stats.totalWorkersNeeded) {
          projectWorkerStatus = "fully_staffed";
        } else if (stats.totalActiveWorkers < stats.totalWorkersNeeded) {
          projectWorkerStatus = "understaffed";
        } else {
          projectWorkerStatus = "overstaffed";
        }

        if (!filters.workerStatus.includes(projectWorkerStatus)) {
          return false;
        }
      }

      return true;
    });

    // Apply sorting
    filtered.sort((a, b) => {
      let compareResult = 0;

      switch (sortField) {
        case "name":
          compareResult = a.name.localeCompare(b.name);
          break;
        case "customer":
          compareResult = a.customer.localeCompare(b.customer);
          break;
        case "type":
          compareResult = a.type.localeCompare(b.type);
          break;
        case "status":
          compareResult = a.status.localeCompare(b.status);
          break;
        case "manager":
          compareResult = a.managers.localeCompare(b.managers);
          break;
        case "alert":
          // Sort alerts: alerts first, then "-"
          if (a.alert === "-" && b.alert !== "-") compareResult = 1;
          else if (a.alert !== "-" && b.alert === "-") compareResult = -1;
          else compareResult = a.alert.localeCompare(b.alert);
          break;
        case "agency":
          compareResult = a.agency.localeCompare(b.agency);
          break;
        case "workers":
          // Sort by workers ratio (assigned/needed)
          const aParts = a.workers.split("/");
          const bParts = b.workers.split("/");
          const aAssigned = aParts.length > 0 ? Number(aParts[0]) : 0;
          const aNeeded = aParts.length > 1 ? Number(aParts[1]) : 0;
          const bAssigned = bParts.length > 0 ? Number(bParts[0]) : 0;
          const bNeeded = bParts.length > 1 ? Number(bParts[1]) : 0;
          // Calculate ratio, handling NaN and division by zero
          const aRatio =
            !isNaN(aAssigned) && !isNaN(aNeeded) && aNeeded > 0 ? aAssigned / aNeeded : 0;
          const bRatio =
            !isNaN(bAssigned) && !isNaN(bNeeded) && bNeeded > 0 ? bAssigned / bNeeded : 0;
          compareResult = aRatio - bRatio;
          break;
        default:
          compareResult = 0;
      }

      return sortDirection === "asc" ? compareResult : -compareResult;
    });

    return filtered;
  }, [projects, searchQuery, activeTab, filters, sortField, sortDirection, realProjects]);

  const itemsPerPage = 300;
  const showPagination = filteredProjects.length > itemsPerPage;
  const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  // Show all projects if less than 300, otherwise paginate. For grid/map/calendar: show all.
  const currentProjects =
    viewMode === "map" || viewMode === "calendar" || viewMode === "grid" || !showPagination
      ? filteredProjects
      : filteredProjects.slice(startIndex, endIndex);

  // Get unique values for filter dropdowns (using IDs)
  const uniqueCustomers = Array.from(
    new Map(
      projects
        .filter((p) => p.customerId && p.customerId !== "")
        .map((p) => [p.customerId, { value: p.customerId, label: p.customer }]),
    ).values(),
  );

  const uniqueTypes = Array.from(new Set(projects.map((p) => p.type))).map((type) => ({
    value: type,
    label: type,
  }));

  const uniqueStatuses = useMemo(
    () =>
      Array.from(
        new Map(
          realProjects
            .filter((p) => p.statusId && p.statusId !== "")
            .map((p) => {
              const statusConfig = projectStatuses.find((s) => s.id === p.statusId);
              return [p.statusId!, { value: p.statusId!, label: statusConfig?.name || "Unknown" }];
            }),
        ).values(),
      ),
    [realProjects, projectStatuses],
  );

  const uniqueManagers = Array.from(
    new Map(
      realProjects
        .flatMap((p) => p.assignedManagerIds || [])
        .map((managerId) => {
          const manager = users.find((u) => u.uid === managerId);
          return [managerId, { value: managerId, label: manager?.name || "Unknown" }];
        }),
    ).values(),
  );

  const uniqueAgencies = Array.from(
    new Map(
      projects
        .filter((p) => p.agencyId && p.agencyId !== "")
        .map((p) => [p.agencyId, { value: p.agencyId, label: p.agency }]),
    ).values(),
  );

  const uniqueWorkers = workers.map((w) => ({
    value: w.id,
    label: w.name || w.phoneNumber || w.id,
  }));

  // Get unique admins that are assigned to projects (only include admins that exist in users)
  const uniqueAdmins = Array.from(
    new Map(
      realProjects
        .filter((p) => p.assignedAdminId && p.assignedAdminId !== "")
        .map((p) => {
          const admin = users.find((u) => u.uid === p.assignedAdminId);
          return admin
            ? [p.assignedAdminId!, { value: p.assignedAdminId!, label: admin.name }]
            : null;
        })
        .filter((item): item is [string, { value: string; label: string }] => item !== null),
    ).values(),
  );

  return (
    <div className="flex min-h-[calc(100vh-4rem)] flex-col gap-4 md:gap-6">
      {/* Header - Projects title with search and action buttons */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <h1 className="text-xl font-semibold text-gray-900 md:text-2xl">Projects</h1>
        <div className="flex flex-col gap-2 md:flex-row md:items-center">
          <div className="relative w-full md:w-64">
            <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Project search"
              value={localSearch}
              onChange={(e) => {
                const value = e.target.value;
                setLocalSearch(value);
                if (urlSearchDebounceTimer.current) clearTimeout(urlSearchDebounceTimer.current);
                urlSearchDebounceTimer.current = setTimeout(() => {
                  setUrlState({ search: value, page: "1" });
                }, 300);
              }}
              className="h-10 w-full pl-9"
            />
          </div>
          <div className="grid grid-cols-2 gap-2 md:flex">
            <Button
              variant="outlinePrimary"
              size="default"
              onClick={() => setFilterOpen(!filterOpen)}
              className="w-full bg-transparent md:w-auto"
            >
              <Filter className="h-4 w-4" />
              {filterOpen ? "Hide filter" : "Filter"}
            </Button>
            <Link to="/dashboard/admin/projects/create" className="w-full md:w-auto">
              <Button variant="primary" size="default" className="w-full md:w-auto">
                <Plus className="h-4 w-4" />
                Create Project
              </Button>
            </Link>
          </div>
        </div>
      </div>
      {/* Filter Panel */}
      {filterOpen && (
        <div className="mb-6">
          <div className="rounded-lg border border-gray-200 bg-white p-4 md:p-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              {/* Customer Filter */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Customer</label>
                <MultiSelectCombobox
                  value={tempFilters.customers}
                  onChange={(newCustomers) => {
                    setTempFilters({ ...tempFilters, customers: newCustomers });
                  }}
                  options={uniqueCustomers}
                  placeholder="Select customers"
                  searchPlaceholder="Search customers..."
                />
              </div>

              {/* Type Filter */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Type</label>
                <MultiSelectCombobox
                  value={tempFilters.types}
                  onChange={(newTypes) => {
                    setTempFilters({ ...tempFilters, types: newTypes });
                  }}
                  options={uniqueTypes}
                  placeholder="Select type"
                  searchPlaceholder="Search type..."
                />
              </div>

              {/* Status Filter */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Status</label>
                <MultiSelectCombobox
                  value={tempFilters.statuses}
                  onChange={(newStatuses) => {
                    setTempFilters({ ...tempFilters, statuses: newStatuses });
                  }}
                  options={uniqueStatuses}
                  placeholder="Select status"
                  searchPlaceholder="Search status..."
                />
              </div>

              {/* Managers Filter */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Managers</label>
                <MultiSelectCombobox
                  value={tempFilters.managers}
                  onChange={(newManagers) => {
                    setTempFilters({ ...tempFilters, managers: newManagers });
                  }}
                  options={uniqueManagers}
                  placeholder="Select managers"
                  searchPlaceholder="Search managers..."
                />
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              {/* Agency Filter */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Agency</label>
                <MultiSelectCombobox
                  value={tempFilters.agencies}
                  onChange={(newAgencies) => {
                    setTempFilters({ ...tempFilters, agencies: newAgencies });
                  }}
                  options={uniqueAgencies}
                  placeholder="Select agency"
                  searchPlaceholder="Search agency..."
                />
              </div>

              {/* Workers Filter */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Workers</label>
                <MultiSelectCombobox
                  value={tempFilters.workers}
                  onChange={(newWorkers) => {
                    setTempFilters({ ...tempFilters, workers: newWorkers });
                  }}
                  options={uniqueWorkers}
                  placeholder="Select workers"
                  searchPlaceholder="Search workers..."
                />
              </div>

              {/* Project Start Date */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Project Start Date
                </label>
                <Popover open={isStartDatePopoverOpen} onOpenChange={setIsStartDatePopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {projectStartFromDate && projectStartToDate
                        ? `${format(projectStartFromDate, "MMM dd")} - ${format(projectStartToDate, "MMM dd, yyyy")}`
                        : projectStartFromDate
                          ? `From ${format(projectStartFromDate, "MMM dd, yyyy")}`
                          : projectStartToDate
                            ? `To ${format(projectStartToDate, "MMM dd, yyyy")}`
                            : "Select date range"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <div className="space-y-3 p-3">
                      <div className="text-sm font-medium">Select Start Date Range</div>
                      <div className="space-y-2">
                        <div>
                          <label className="text-xs text-gray-600">From Date</label>
                          <CalendarComponent
                            mode="single"
                            selected={projectStartFromDate}
                            onSelect={(date) => {
                              setTempFilters({
                                ...tempFilters,
                                projectStartFrom: date ? dateToDateString(date) : "",
                              });
                            }}
                            className="rounded-md border"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-600">To Date</label>
                          <CalendarComponent
                            mode="single"
                            selected={projectStartToDate}
                            onSelect={(date) => {
                              setTempFilters({
                                ...tempFilters,
                                projectStartTo: date ? dateToDateString(date) : "",
                              });
                            }}
                            disabled={(date) =>
                              projectStartFromDate ? date < projectStartFromDate : false
                            }
                            className="rounded-md border"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => setIsStartDatePopoverOpen(false)}>
                          Apply
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setTempFilters({
                              ...tempFilters,
                              projectStartFrom: "",
                              projectStartTo: "",
                            });
                          }}
                        >
                          Clear
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Project End Date */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Project End Date
                </label>
                <Popover open={isEndDatePopoverOpen} onOpenChange={setIsEndDatePopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {projectEndFromDate && projectEndToDate
                        ? `${format(projectEndFromDate, "MMM dd")} - ${format(projectEndToDate, "MMM dd, yyyy")}`
                        : projectEndFromDate
                          ? `From ${format(projectEndFromDate, "MMM dd, yyyy")}`
                          : projectEndToDate
                            ? `To ${format(projectEndToDate, "MMM dd, yyyy")}`
                            : "Select date range"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <div className="space-y-3 p-3">
                      <div className="text-sm font-medium">Select End Date Range</div>
                      <div className="space-y-2">
                        <div>
                          <label className="text-xs text-gray-600">From Date</label>
                          <CalendarComponent
                            mode="single"
                            selected={projectEndFromDate}
                            onSelect={(date) => {
                              setTempFilters({
                                ...tempFilters,
                                projectEndFrom: date ? dateToDateString(date) : "",
                              });
                            }}
                            className="rounded-md border"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-600">To Date</label>
                          <CalendarComponent
                            mode="single"
                            selected={projectEndToDate}
                            onSelect={(date) => {
                              setTempFilters({
                                ...tempFilters,
                                projectEndTo: date ? dateToDateString(date) : "",
                              });
                            }}
                            disabled={(date) =>
                              projectEndFromDate ? date < projectEndFromDate : false
                            }
                            className="rounded-md border"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => setIsEndDatePopoverOpen(false)}>
                          Apply
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setTempFilters({
                              ...tempFilters,
                              projectEndFrom: "",
                              projectEndTo: "",
                            });
                          }}
                        >
                          Clear
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              {/* Admin Filter */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Admin</label>
                <MultiSelectCombobox
                  value={tempFilters.admin}
                  onChange={(newAdmin) => {
                    setTempFilters({ ...tempFilters, admin: newAdmin });
                  }}
                  options={uniqueAdmins}
                  placeholder="Select admin"
                  searchPlaceholder="Search admin..."
                />
              </div>

              {/* Worker Status Filter */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Worker Status
                </label>
                <MultiSelectCombobox
                  value={tempFilters.workerStatus}
                  onChange={(newWorkerStatus) => {
                    setTempFilters({ ...tempFilters, workerStatus: newWorkerStatus });
                  }}
                  options={[
                    { value: "fully_staffed", label: "Fully Staffed" },
                    { value: "understaffed", label: "Understaffed" },
                    { value: "overstaffed", label: "Overstaffed" },
                    { value: "no_work_units", label: "No Work Units" },
                  ]}
                  placeholder="Select worker status"
                  searchPlaceholder="Search status..."
                />
              </div>

              {/* Show only with alert checkbox */}
              <div className="flex items-end">
                <label className="flex items-center gap-2 pb-2">
                  <Checkbox
                    checked={tempFilters.showOnlyWithAlert}
                    onCheckedChange={(checked) =>
                      setTempFilters({ ...tempFilters, showOnlyWithAlert: checked })
                    }
                  />
                  <span className="text-sm text-gray-700">Show only with alert</span>
                </label>
              </div>

              {/* Show hidden checkbox */}
              <div className="flex items-end">
                <label className="flex items-center gap-2 pb-2">
                  <Checkbox
                    checked={tempFilters.showHidden}
                    onCheckedChange={(checked) =>
                      setTempFilters({ ...tempFilters, showHidden: checked })
                    }
                  />
                  <span className="text-sm text-gray-700">Show hidden</span>
                </label>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex flex-row justify-end gap-2">
              <Button
                variant="outlineDestructive"
                onClick={resetFilters}
                className="flex-1 md:flex-none"
              >
                Reset
              </Button>
              <Button
                variant="outlinePrimary"
                onClick={applyFilters}
                className="flex-1 md:flex-none"
              >
                Apply filter
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Filter Chips */}
      {hasActiveFilters() && (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
          <div className="flex flex-col gap-3">
            <div className="flex flex-1 flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Filtered by:</span>
              {filters.customers.length > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-3 py-1 text-sm text-[#1D293D]">
                      {filters.customers
                        .map((id) => users.find((u) => u.uid === id)?.name || id)
                        .join(", ")}
                      <button
                        onClick={() => {
                          setUrlState({ customers: [] });
                          setTempFilters({ ...tempFilters, customers: [] });
                        }}
                        className="ml-1 text-[#1D293D] hover:text-gray-900"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Customer</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {filters.types.length > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-3 py-1 text-sm text-[#1D293D]">
                      {filters.types.join(", ")}
                      <button
                        onClick={() => {
                          setUrlState({ types: [] });
                          setTempFilters({ ...tempFilters, types: [] });
                        }}
                        className="ml-1 text-[#1D293D] hover:text-gray-900"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Type</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {filters.statuses.length > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-3 py-1 text-sm text-[#1D293D]">
                      {filters.statuses
                        .map((id) => projectStatuses.find((s) => s.id === id)?.name || id)
                        .join(", ")}
                      <button
                        onClick={() => {
                          setUrlState({ statuses: [] });
                          setTempFilters({ ...tempFilters, statuses: [] });
                        }}
                        className="ml-1 text-[#1D293D] hover:text-gray-900"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Status</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {filters.managers.length > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-3 py-1 text-sm text-[#1D293D]">
                      {filters.managers
                        .map((id) => users.find((u) => u.uid === id)?.name || id)
                        .join(", ")}
                      <button
                        onClick={() => {
                          setUrlState({ managers: [] });
                          setTempFilters({ ...tempFilters, managers: [] });
                        }}
                        className="ml-1 text-[#1D293D] hover:text-gray-900"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Managers</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {filters.agencies.length > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-3 py-1 text-sm text-[#1D293D]">
                      {filters.agencies
                        .map((id) => agencies.find((a) => a.id === id)?.name || id)
                        .join(", ")}
                      <button
                        onClick={() => {
                          setUrlState({ agencies: [] });
                          setTempFilters({ ...tempFilters, agencies: [] });
                        }}
                        className="ml-1 text-[#1D293D] hover:text-gray-900"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Agency</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {filters.workers.length > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-3 py-1 text-sm text-[#1D293D]">
                      {filters.workers
                        .map((id) => {
                          const worker = workers.find((w) => w.id === id);
                          return worker?.name || worker?.phoneNumber || id;
                        })
                        .join(", ")}
                      <button
                        onClick={() => {
                          setUrlState({ workers: [] });
                          setTempFilters({ ...tempFilters, workers: [] });
                        }}
                        className="ml-1 text-[#1D293D] hover:text-gray-900"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Workers</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {(filters.projectStartFrom || filters.projectStartTo) && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-3 py-1 text-sm text-[#1D293D]">
                      Start: {filters.projectStartFrom || "..."} - {filters.projectStartTo || "..."}
                      <button
                        onClick={() => {
                          setUrlState({ projectStartFrom: "", projectStartTo: "" });
                          setTempFilters({
                            ...tempFilters,
                            projectStartFrom: "",
                            projectStartTo: "",
                          });
                        }}
                        className="ml-1 text-[#1D293D] hover:text-gray-900"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Project Start Date Range</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {(filters.projectEndFrom || filters.projectEndTo) && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-3 py-1 text-sm text-[#1D293D]">
                      End: {filters.projectEndFrom || "..."} - {filters.projectEndTo || "..."}
                      <button
                        onClick={() => {
                          setUrlState({ projectEndFrom: "", projectEndTo: "" });
                          setTempFilters({ ...tempFilters, projectEndFrom: "", projectEndTo: "" });
                        }}
                        className="ml-1 text-[#1D293D] hover:text-gray-900"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Project End Date Range</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {filters.workerStatus.length > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-3 py-1 text-sm text-[#1D293D]">
                      {filters.workerStatus
                        .map((status) => {
                          switch (status) {
                            case "fully_staffed":
                              return "Fully Staffed";
                            case "understaffed":
                              return "Understaffed";
                            case "overstaffed":
                              return "Overstaffed";
                            case "no_work_units":
                              return "No Work Units";
                            default:
                              return status;
                          }
                        })
                        .join(", ")}
                      <button
                        onClick={() => {
                          setUrlState({ workerStatus: [] });
                          setTempFilters({ ...tempFilters, workerStatus: [] });
                        }}
                        className="ml-1 text-[#1D293D] hover:text-gray-900"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Worker Status</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {filters.showOnlyWithAlert && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-3 py-1 text-sm text-[#1D293D]">
                      Only with alert
                      <button
                        onClick={() => {
                          setUrlState({ showOnlyWithAlert: false });
                          setTempFilters({ ...tempFilters, showOnlyWithAlert: false });
                        }}
                        className="ml-1 text-[#1D293D] hover:text-gray-900"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Alert Filter</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {filters.showHidden && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-3 py-1 text-sm text-[#1D293D]">
                      Show hidden
                      <button
                        onClick={() => {
                          setUrlState({ showHidden: false });
                          setTempFilters({ ...tempFilters, showHidden: false });
                        }}
                        className="ml-1 text-[#1D293D] hover:text-gray-900"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Show Hidden Filter</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            <div className="flex items-center justify-between gap-4 border-t border-gray-200 pt-3">
              <span className="text-sm text-gray-600">
                <span className="font-semibold text-gray-900">Results Found: </span>
                <span className="text-primary font-semibold">{filteredProjects.length}</span>
              </span>
              <button
                onClick={resetFilters}
                className="text-[#62748E]-600 hover:text-[#62748E]-700 flex items-center gap-1 text-sm"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.4425 2.5H2.50003C2.33886 2.50006 2.18118 2.54685 2.04607 2.6347C1.91096 2.72255 1.80422 2.84769 1.73878 2.99497C1.67334 3.14225 1.65201 3.30534 1.67738 3.46449C1.70274 3.62364 1.77371 3.77203 1.88169 3.89167L7.90252 10.5492C8.17964 10.8554 8.33317 11.2537 8.33336 11.6667V16.6667C8.33329 16.8215 8.37637 16.9733 8.45777 17.1051C8.53917 17.2368 8.65566 17.3433 8.79419 17.4125L10.4609 18.2458C10.5879 18.3093 10.7291 18.3393 10.8711 18.3329C11.013 18.3264 11.1509 18.2838 11.2717 18.2091C11.3925 18.1344 11.4922 18.03 11.5614 17.9059C11.6305 17.7818 11.6668 17.6421 11.6667 17.5V11.6667C11.6669 11.2537 11.8204 10.8554 12.0975 10.5492L12.4534 10.155M13.7501 2.91667L17.9168 7.08333M17.9168 2.91667L13.7501 7.08333"
                    stroke="#62748E"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Clear filter set
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-1 flex-col gap-2">
        {/* Tabs and View Mode Icons */}
        <div className="mb-0">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex gap-1 overflow-x-auto md:gap-2">
              <button
                onClick={() => {
                  setUrlState({ tab: "all", page: "1" });
                }}
                className={`hover:bg-accent hover:text-accent-foreground rounded-t-md border-b-2 px-2 py-2 text-sm font-medium transition-colors ${
                  activeTab === "all"
                    ? "border-[#7C12C1] text-gray-900"
                    : "border-transparent text-gray-500 hover:border-slate-300"
                }`}
              >
                All Projects
              </button>
              <button
                onClick={() => {
                  setUrlState({ tab: "ongoing", page: "1" });
                }}
                className={`hover:bg-accent hover:text-accent-foreground rounded-t-md border-b-2 px-2 py-2 text-sm font-medium transition-colors ${
                  activeTab === "ongoing"
                    ? "border-[#7C12C1] text-gray-900"
                    : "border-transparent text-gray-500 hover:border-slate-300"
                }`}
              >
                Ongoing
              </button>
              <button
                onClick={() => {
                  setUrlState({ tab: "onetime", page: "1" });
                }}
                className={`hover:bg-accent hover:text-accent-foreground rounded-t-md border-b-2 px-2 py-2 text-sm font-medium transition-colors ${
                  activeTab === "onetime"
                    ? "border-[#7C12C1] text-gray-900"
                    : "border-transparent text-gray-500 hover:border-slate-300"
                }`}
              >
                One-time
              </button>
            </div>
            <div className="flex items-center justify-end gap-2 md:justify-start">
              <button
                onClick={() => setViewMode("list")}
                className={`flex h-9 w-9 items-center justify-center rounded-md border transition-colors ${
                  viewMode === "list"
                    ? "border-[#7C12C1] bg-white"
                    : "border-gray-300 bg-transparent hover:bg-gray-50"
                }`}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 2V14M2 6H14M2 10H14M3.33333 2H12.6667C13.403 2 14 2.59695 14 3.33333V12.6667C14 13.403 13.403 14 12.6667 14H3.33333C2.59695 14 2 13.403 2 12.6667V3.33333C2 2.59695 2.59695 2 3.33333 2Z"
                    stroke={viewMode === "list" ? "#7C12C1" : "#020618"}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                onClick={() => setViewMode("grid")}
                className={`flex h-9 w-9 items-center justify-center rounded-md border transition-colors ${
                  viewMode === "grid"
                    ? "border-[#7C12C1] bg-white"
                    : "border-gray-300 bg-transparent hover:bg-gray-50"
                }`}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 2H2.66667C2.29848 2 2 2.29848 2 2.66667V6C2 6.36819 2.29848 6.66667 2.66667 6.66667H6C6.36819 6.66667 6.66667 6.36819 6.66667 6V2.66667C6.66667 2.29848 6.36819 2 6 2Z"
                    stroke={viewMode === "grid" ? "#7C12C1" : "#020618"}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M13.3333 2H10C9.63181 2 9.33333 2.29848 9.33333 2.66667V6C9.33333 6.36819 9.63181 6.66667 10 6.66667H13.3333C13.7015 6.66667 14 6.36819 14 6V2.66667C14 2.29848 13.7015 2 13.3333 2Z"
                    stroke={viewMode === "grid" ? "#7C12C1" : "#020618"}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M13.3333 9.33333H10C9.63181 9.33333 9.33333 9.63181 9.33333 10V13.3333C9.33333 13.7015 9.63181 14 10 14H13.3333C13.7015 14 14 13.7015 14 13.3333V10C14 9.63181 13.7015 9.33333 13.3333 9.33333Z"
                    stroke={viewMode === "grid" ? "#7C12C1" : "#020618"}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6 9.33333H2.66667C2.29848 9.33333 2 9.63181 2 10V13.3333C2 13.7015 2.29848 14 2.66667 14H6C6.36819 14 6.66667 13.7015 6.66667 13.3333V10C6.66667 9.63181 6.36819 9.33333 6 9.33333Z"
                    stroke={viewMode === "grid" ? "#7C12C1" : "#020618"}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                onClick={() => setViewMode("map")}
                className={`flex h-9 w-9 items-center justify-center rounded-md border transition-colors ${
                  viewMode === "map"
                    ? "border-[#7C12C1] bg-white"
                    : "border-gray-300 bg-transparent hover:bg-gray-50"
                }`}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 3.84247C9.79312 3.84247 9.58907 3.79433 9.404 3.70185L6.596 2.29785C6.41094 2.20537 6.20689 2.15723 6 2.15723M10 3.84247C10.2069 3.84247 10.4109 3.79433 10.596 3.70185L13.0353 2.48185C13.1371 2.43102 13.2501 2.40705 13.3637 2.41223C13.4773 2.4174 13.5876 2.45154 13.6843 2.5114C13.781 2.57126 13.8607 2.65486 13.916 2.75424C13.9713 2.85361 14.0002 2.96547 14 3.07918V11.5885C13.9999 11.7123 13.9654 11.8336 13.9003 11.9389C13.8352 12.0441 13.7421 12.1292 13.6313 12.1845L10.596 13.7025C10.4109 13.795 10.2069 13.8431 10 13.8431C9.79312 13.8431 9.58907 13.795 9.404 13.7025L6.596 12.2985C6.41094 12.206 6.20689 12.1579 6 12.1579C5.79312 12.1579 5.58907 12.206 5.404 12.2985L2.96467 13.5185C2.8629 13.5694 2.74982 13.5933 2.63617 13.5881C2.52253 13.5829 2.41211 13.5487 2.31541 13.4888C2.21872 13.4288 2.13898 13.3452 2.08377 13.2457C2.02856 13.1462 1.99972 13.0343 2 12.9205V4.41185C2.00007 4.28807 2.03459 4.16676 2.0997 4.0615C2.16482 3.95623 2.25795 3.87118 2.36867 3.81585L5.404 2.29785C5.58907 2.20537 5.79312 2.15723 6 2.15723M10 3.84247L10 13.8424M6 2.15723L6 12.1572"
                    stroke={viewMode === "map" ? "#7C12C1" : "#020618"}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                onClick={() => setViewMode("calendar")}
                className={`flex h-9 w-9 items-center justify-center rounded-md border transition-colors ${
                  viewMode === "calendar"
                    ? "border-purple-600 bg-white"
                    : "border-gray-300 bg-transparent hover:bg-gray-50"
                }`}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.33333 1.3335V4.00016M10.6667 1.3335V4.00016M2 6.66683H14M3.33333 2.66683H12.6667C13.403 2.66683 14 3.26378 14 4.00016V13.3335C14 14.0699 13.403 14.6668 12.6667 14.6668H3.33333C2.59695 14.6668 2 14.0699 2 13.3335V4.00016C2 3.26378 2.59695 2.66683 3.33333 2.66683Z"
                    stroke={viewMode === "calendar" ? "#7C12C1" : "#020618"}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Content Area - Flex 1 to push pagination down */}
        <div className="flex-1 space-y-3">
          {/* Loading State */}
          {(projectResource.tag === "loading" ||
            agenciesResource.tag === "loading" ||
            workersResource.tag === "loading") && (
            <>
              {viewMode === "list" && <ProjectsListShimmer />}
              {viewMode === "grid" && <ProjectsGridShimmer />}
              {(viewMode === "map" || viewMode === "calendar") && <ProjectsGridShimmer />}
            </>
          )}

          {/* Table View */}
          {viewMode === "list" &&
            projectResource.tag !== "loading" &&
            agenciesResource.tag !== "loading" &&
            workersResource.tag !== "loading" &&
            (currentProjects.length === 0 ? (
              <EmptyProjectsState />
            ) : (
              <div className="flex min-h-[calc(100vh-400px)] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-b border-gray-200 bg-gray-50">
                        <TableHead className="h-12 font-medium text-gray-700">
                          <button
                            className="flex cursor-pointer items-center gap-1 hover:text-gray-900"
                            onClick={() => handleSort("name")}
                          >
                            Name
                            <svg
                              className={`h-4 w-4 transition-opacity ${sortField === "name" ? "opacity-100" : "opacity-40"}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              {sortField === "name" && sortDirection === "asc" ? (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 16V4m0 0L3 8m4-4l4 4"
                                />
                              ) : sortField === "name" && sortDirection === "desc" ? (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M17 20V8m0 0l-4 4m4-4l4 4"
                                />
                              ) : (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                                />
                              )}
                            </svg>
                          </button>
                        </TableHead>
                        <TableHead className="h-12 font-medium text-gray-700">
                          <button
                            className="flex cursor-pointer items-center gap-1 hover:text-gray-900"
                            onClick={() => handleSort("customer")}
                          >
                            Customer
                            <svg
                              className={`h-4 w-4 transition-opacity ${sortField === "customer" ? "opacity-100" : "opacity-40"}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              {sortField === "customer" && sortDirection === "asc" ? (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 16V4m0 0L3 8m4-4l4 4"
                                />
                              ) : sortField === "customer" && sortDirection === "desc" ? (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M17 20V8m0 0l-4 4m4-4l4 4"
                                />
                              ) : (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                                />
                              )}
                            </svg>
                          </button>
                        </TableHead>
                        <TableHead className="h-12 font-medium text-gray-700">
                          <button
                            className="flex cursor-pointer items-center gap-1 hover:text-gray-900"
                            onClick={() => handleSort("type")}
                          >
                            Type
                            <svg
                              className={`h-4 w-4 transition-opacity ${sortField === "type" ? "opacity-100" : "opacity-40"}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              {sortField === "type" && sortDirection === "asc" ? (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 16V4m0 0L3 8m4-4l4 4"
                                />
                              ) : sortField === "type" && sortDirection === "desc" ? (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M17 20V8m0 0l-4 4m4-4l4 4"
                                />
                              ) : (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                                />
                              )}
                            </svg>
                          </button>
                        </TableHead>
                        <TableHead className="h-12 font-medium text-gray-700">
                          <button
                            className="flex cursor-pointer items-center gap-1 hover:text-gray-900"
                            onClick={() => handleSort("status")}
                          >
                            Status
                            <svg
                              className={`h-4 w-4 transition-opacity ${sortField === "status" ? "opacity-100" : "opacity-40"}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              {sortField === "status" && sortDirection === "asc" ? (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 16V4m0 0L3 8m4-4l4 4"
                                />
                              ) : sortField === "status" && sortDirection === "desc" ? (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M17 20V8m0 0l-4 4m4-4l4 4"
                                />
                              ) : (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                                />
                              )}
                            </svg>
                          </button>
                        </TableHead>
                        <TableHead className="h-12 font-medium text-gray-700">
                          <button
                            className="flex cursor-pointer items-center gap-1 hover:text-gray-900"
                            onClick={() => handleSort("manager")}
                          >
                            Managers
                            <svg
                              className={`h-4 w-4 transition-opacity ${sortField === "manager" ? "opacity-100" : "opacity-40"}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              {sortField === "manager" && sortDirection === "asc" ? (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 16V4m0 0L3 8m4-4l4 4"
                                />
                              ) : sortField === "manager" && sortDirection === "desc" ? (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M17 20V8m0 0l-4 4m4-4l4 4"
                                />
                              ) : (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                                />
                              )}
                            </svg>
                          </button>
                        </TableHead>
                        <TableHead className="h-12 font-medium text-gray-700">
                          <button
                            className="flex cursor-pointer items-center gap-1 hover:text-gray-900"
                            onClick={() => handleSort("alert")}
                          >
                            Alert
                            <svg
                              className={`h-4 w-4 transition-opacity ${sortField === "alert" ? "opacity-100" : "opacity-40"}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              {sortField === "alert" && sortDirection === "asc" ? (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 16V4m0 0L3 8m4-4l4 4"
                                />
                              ) : sortField === "alert" && sortDirection === "desc" ? (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M17 20V8m0 0l-4 4m4-4l4 4"
                                />
                              ) : (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                                />
                              )}
                            </svg>
                          </button>
                        </TableHead>
                        <TableHead className="h-12 font-medium text-gray-700">
                          <button
                            className="flex cursor-pointer items-center gap-1 hover:text-gray-900"
                            onClick={() => handleSort("agency")}
                          >
                            Agency
                            <svg
                              className={`h-4 w-4 transition-opacity ${sortField === "agency" ? "opacity-100" : "opacity-40"}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              {sortField === "agency" && sortDirection === "asc" ? (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 16V4m0 0L3 8m4-4l4 4"
                                />
                              ) : sortField === "agency" && sortDirection === "desc" ? (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M17 20V8m0 0l-4 4m4-4l4 4"
                                />
                              ) : (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                                />
                              )}
                            </svg>
                          </button>
                        </TableHead>
                        <TableHead className="h-12 font-medium text-gray-700">
                          <button
                            className="flex cursor-pointer items-center gap-1 hover:text-gray-900"
                            onClick={() => handleSort("workers")}
                          >
                            Workers
                            <svg
                              className={`h-4 w-4 transition-opacity ${sortField === "workers" ? "opacity-100" : "opacity-40"}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              {sortField === "workers" && sortDirection === "asc" ? (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 16V4m0 0L3 8m4-4l4 4"
                                />
                              ) : sortField === "workers" && sortDirection === "desc" ? (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M17 20V8m0 0l-4 4m4-4l4 4"
                                />
                              ) : (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                                />
                              )}
                            </svg>
                          </button>
                        </TableHead>
                        {visibleColumns.startDate && (
                          <TableHead className="h-12 font-medium text-gray-700">
                            Project Date
                          </TableHead>
                        )}
                        <TableHead className="h-12 w-12 border-l border-gray-200 font-medium text-gray-700">
                          {/* Settings Dropdown for Column Visibility */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="flex h-8 w-8 items-center justify-center rounded hover:bg-gray-100">
                                <svg
                                  width="17"
                                  height="19"
                                  viewBox="0 0 17 19"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M15.75 12.415V5.74829C15.7497 5.45602 15.6725 5.16897 15.5263 4.91593C15.38 4.66289 15.1698 4.45276 14.9167 4.30662L9.08333 0.973291C8.82997 0.82701 8.54256 0.75 8.25 0.75C7.95744 0.75 7.67003 0.82701 7.41667 0.973291L1.58333 4.30662C1.33022 4.45276 1.11998 4.66289 0.973718 4.91593C0.827452 5.16897 0.7503 5.45602 0.75 5.74829V12.415C0.7503 12.7072 0.827452 12.9943 0.973718 13.2473C1.11998 13.5004 1.33022 13.7105 1.58333 13.8566L7.41667 17.19C7.67003 17.3362 7.95744 17.4133 8.25 17.4133C8.54256 17.4133 8.82997 17.3362 9.08333 17.19L14.9167 13.8566C15.1698 13.7105 15.38 13.5004 15.5263 13.2473C15.6725 12.9943 15.7497 12.7072 15.75 12.415Z"
                                    stroke="#020618"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M8.25 12.415C10.0909 12.415 11.5833 10.9227 11.5833 9.08171C11.5833 7.24076 10.0909 5.74837 8.25 5.74837C6.40905 5.74837 4.91667 7.24076 4.91667 9.08171C4.91667 10.9227 6.40905 12.415 8.25 12.415Z"
                                    stroke="#020618"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48 p-1">
                              <div
                                className="rounded-sm px-2 py-1.5 hover:bg-gray-50"
                                onSelect={(e: any) => e.preventDefault()}
                              >
                                <Checkbox
                                  checked={visibleColumns.customer}
                                  onCheckedChange={() => toggleColumn("customer")}
                                  label="Customer"
                                />
                              </div>
                              <div
                                className="rounded-sm px-2 py-1.5 hover:bg-gray-50"
                                onSelect={(e: any) => e.preventDefault()}
                              >
                                <Checkbox
                                  checked={visibleColumns.agency}
                                  onCheckedChange={() => toggleColumn("agency")}
                                  label="Agency"
                                />
                              </div>
                              <div
                                className="rounded-sm px-2 py-1.5 hover:bg-gray-50"
                                onSelect={(e: any) => e.preventDefault()}
                              >
                                <Checkbox
                                  checked={visibleColumns.manager}
                                  onCheckedChange={() => toggleColumn("manager")}
                                  label="Managers"
                                />
                              </div>
                              <div
                                className="rounded-sm px-2 py-1.5 hover:bg-gray-50"
                                onSelect={(e: any) => e.preventDefault()}
                              >
                                <Checkbox
                                  checked={visibleColumns.workers}
                                  onCheckedChange={() => toggleColumn("workers")}
                                  label="Workers"
                                />
                              </div>
                              <div
                                className="rounded-sm px-2 py-1.5 hover:bg-gray-50"
                                onSelect={(e: any) => e.preventDefault()}
                              >
                                <Checkbox
                                  checked={visibleColumns.startDate}
                                  onCheckedChange={() => toggleColumn("startDate")}
                                  label="Project Date"
                                />
                              </div>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {currentProjects.length === 0 ? (
                        <TableRow className="h-full">
                          <TableCell colSpan={10} className="h-full">
                            <div className="flex min-h-[calc(100vh-500px)] flex-col items-center justify-center">
                              <svg
                                width="160"
                                height="80"
                                viewBox="0 0 160 80"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M66.5 16C64.0147 16 62 18.0147 62 20.5C62 22.9853 64.0147 25 66.5 25H40.5C38.0147 25 36 27.0147 36 29.5C36 31.9853 38.0147 34 40.5 34H56.5C58.9853 34 61 36.0147 61 38.5C61 40.9853 58.9853 43 56.5 43H32.5C30.0147 43 28 45.0147 28 47.5C28 49.9853 30.0147 52 32.5 52H56.5C54.0147 52 52 54.0147 52 56.5C52 58.9853 54.0147 61 56.5 61H112.5C114.985 61 117 58.9853 117 56.5C117 54.0147 114.985 52 112.5 52H111.25C108.765 52 106.75 49.9853 106.75 47.5C106.75 45.0147 108.765 43 111.25 43H117.5C119.985 43 122 40.9853 122 38.5C122 36.0147 119.985 34 117.5 34H103.5C105.985 34 108 31.9853 108 29.5C108 27.0147 105.985 25 103.5 25H128.5C130.985 25 133 22.9853 133 20.5C133 18.0147 130.985 16 128.5 16H66.5Z"
                                  fill="#EDE9FE"
                                />
                                <path
                                  d="M128.5 43C130.985 43 133 40.9853 133 38.5C133 36.0147 130.985 34 128.5 34C126.015 34 124 36.0147 124 38.5C124 40.9853 126.015 43 128.5 43Z"
                                  fill="#EDE9FE"
                                />
                                <path
                                  d="M50.1855 20.75H66.5684C67.9292 20.75 69.032 21.8531 69.0322 23.2139C69.0322 25.4033 70.8077 27.1787 72.9971 27.1787H99.8145C102.264 27.1787 104.25 29.164 104.25 31.6133V68.8145C104.25 71.2639 102.264 73.25 99.8145 73.25H50.1855C47.7361 73.25 45.75 71.2639 45.75 68.8145V25.1855C45.75 22.7361 47.7361 20.75 50.1855 20.75Z"
                                  fill="white"
                                  stroke="#7F22FE"
                                  strokeWidth="1.5"
                                />
                                <path
                                  d="M65 27.5926C65 26.1607 63.8393 25 62.4074 25H52.5926C51.1607 25 50 26.1607 50 27.5926V61H100V33.5926C100 32.1607 98.8393 31 97.4074 31H67.5926C66.1607 31 65 29.8393 65 28.4074V27.5926Z"
                                  fill="#EDE9FE"
                                />
                                <path
                                  d="M60.6857 34.7247H109.944C112.379 34.7247 113.854 36.6219 113.247 38.9746L105.527 68.893C104.913 71.2735 102.404 73.2144 99.9402 73.2146H50.6805C48.2457 73.2144 46.7722 71.3173 47.3792 68.9647L55.0987 39.0463C55.7129 36.6657 58.2217 34.7248 60.6857 34.7247Z"
                                  fill="white"
                                  stroke="#7F22FE"
                                  strokeWidth="1.5"
                                />
                                <path
                                  d="M101 66.9328C83.9415 64.4612 70.9507 53.175 64.8947 38L63.2075 38C61.7636 37.9982 60.2875 39.087 59.8968 40.4419L52.0958 67.4943C51.6983 68.8729 52.5747 70 54.0442 70L97.5201 69.9999C98.9752 69.9999 100.459 68.8941 100.836 67.529L101 66.9328Z"
                                  fill="#EDE9FE"
                                />
                                <circle
                                  cx="75.8091"
                                  cy="7.24074"
                                  r="2.54074"
                                  stroke="#8E51FF"
                                  strokeWidth="1.4"
                                />
                                <path
                                  d="M80.8358 14.8615L86.3355 20.3612M86.3355 14.8615L80.8358 20.3612"
                                  stroke="#8E51FF"
                                  strokeWidth="1.4"
                                  strokeLinecap="round"
                                />
                                <circle cx="97.2668" cy="17.0666" r="2.8" fill="#8E51FF" />
                              </svg>
                              <h3 className="mt-4 text-lg font-semibold text-gray-900">
                                No match found
                              </h3>
                              <p className="mt-1 text-sm text-gray-600">
                                Try changing the filter settings
                              </p>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        currentProjects.map((project) => {
                          return (
                            <TableRow
                              key={project.id}
                              className="group hover:bg-muted cursor-pointer border-b border-gray-100 transition-colors [&>td]:rounded-none"
                              onClick={() => {
                                navigate({
                                  to: "/dashboard/admin/projects/$projectId",
                                  params: { projectId: project.id },
                                });
                              }}
                            >
                              <TableCell className="rounded-none text-gray-700 first:rounded-none">
                                <span className="hover:text-[#7C12C1]">{project.name}</span>
                              </TableCell>
                              <TableCell className="rounded-none text-gray-700">
                                {project.customer}
                              </TableCell>
                              <TableCell className="rounded-none text-gray-700">
                                <StatusBadge
                                  label={project.type}
                                  color={project.type === "Ongoing" ? "blue" : "orange"}
                                  type="type1"
                                  state="fill"
                                />
                              </TableCell>
                              <TableCell className="rounded-none">
                                {project.status && (
                                  <div onClick={(e) => e.stopPropagation()}>
                                    <StatusChangePopover
                                      currentStatusId={
                                        realProjects.find((rp) => rp.id === project.id)?.statusId ||
                                        null
                                      }
                                      statuses={projectStatuses}
                                      onStatusChange={(statusId) =>
                                        handleStatusChange(project.id, statusId)
                                      }
                                    >
                                      <button className="cursor-pointer transition-opacity hover:opacity-80">
                                        <Badge
                                          style={{
                                            backgroundColor: project.statusColor,
                                            color: "white",
                                          }}
                                          className="border-0"
                                        >
                                          {project.status}
                                        </Badge>
                                      </button>
                                    </StatusChangePopover>
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className="rounded-none text-gray-700">
                                {project.managers}
                              </TableCell>
                              <TableCell className="rounded-none">
                                {project.alert !== "-" ? (
                                  <div className="flex items-center gap-2 rounded-md border border-orange-300 bg-orange-50 px-2 py-1">
                                    <svg
                                      className="h-4 w-4 text-orange-500"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                      />
                                    </svg>
                                    <span className="text-xs font-medium text-orange-700">
                                      {project.alert}
                                    </span>
                                  </div>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </TableCell>
                              <TableCell className="rounded-none text-gray-700">
                                {project.agency}
                              </TableCell>
                              <TableCell className="rounded-none text-gray-700">
                                {project.workers}
                              </TableCell>
                              {visibleColumns.startDate && (
                                <TableCell className="rounded-none text-gray-700">
                                  {project.startDate && project.endDate
                                    ? `${project.startDate.toDate().toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: "America/New_York" })} - ${project.endDate.toDate().toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: "America/New_York" })}`
                                    : project.startDate
                                      ? project.startDate.toDate().toLocaleDateString("en-US", {
                                          year: "numeric",
                                          month: "2-digit",
                                          day: "2-digit",
                                          timeZone: "America/New_York",
                                        })
                                      : project.endDate
                                        ? project.endDate.toDate().toLocaleDateString("en-US", {
                                            year: "numeric",
                                            month: "2-digit",
                                            day: "2-digit",
                                            timeZone: "America/New_York",
                                          })
                                        : "-"}
                                </TableCell>
                              )}
                              <TableCell
                                className="rounded-none border-l border-gray-200 last:rounded-none"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {/* Three Dot Menu */}
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-gray-100">
                                      <svg
                                        className="h-4 w-4 text-gray-600"
                                        fill="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <circle cx="12" cy="5" r="2" />
                                        <circle cx="12" cy="12" r="2" />
                                        <circle cx="12" cy="19" r="2" />
                                      </svg>
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-40">
                                    <DropdownMenuItem
                                      className="flex cursor-pointer items-center gap-2"
                                      onClick={() => {
                                        navigate({
                                          to: "/dashboard/admin/projects/$projectId",
                                          params: { projectId: project.id },
                                          search: { edit: true },
                                        });
                                      }}
                                    >
                                      <svg
                                        width="15"
                                        height="15"
                                        viewBox="0 0 15 15"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M8.08333 13.417H13.4167M9.41667 2.75033L12.0833 5.41699M13.5326 3.95825C13.8851 3.60587 14.0831 3.1279 14.0832 2.62949C14.0833 2.13108 13.8853 1.65306 13.533 1.30059C13.1806 0.948115 12.7026 0.750063 12.2042 0.75C11.7058 0.749937 11.2278 0.94787 10.8753 1.30025L1.97796 10.1996C1.82317 10.3539 1.7087 10.5439 1.64462 10.7529L0.763956 13.6543C0.746726 13.7119 0.745425 13.7732 0.76019 13.8315C0.774956 13.8898 0.805236 13.9431 0.84782 13.9856C0.890405 14.0281 0.943703 14.0583 1.00206 14.073C1.06042 14.0876 1.12166 14.0862 1.17929 14.0689L4.08129 13.1889C4.29007 13.1254 4.48007 13.0116 4.63462 12.8576L13.5326 3.95825Z"
                                          stroke="#020618"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                      <span className="text-gray-900">Edit</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      className="flex cursor-pointer items-center gap-2"
                                      onClick={() => handleDeleteProject(project.id)}
                                      disabled={deleting}
                                    >
                                      <svg
                                        width="14"
                                        height="15"
                                        viewBox="0 0 14 15"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M5.41667 6.75V10.75M8.08333 6.75V10.75M11.4167 3.41667V12.75C11.4167 13.1036 11.2762 13.4428 11.0261 13.6928C10.7761 13.9429 10.437 14.0833 10.0833 14.0833H3.41667C3.06304 14.0833 2.72391 13.9429 2.47386 13.6928C2.22381 13.4428 2.08333 13.1036 2.08333 12.75V3.41667M0.75 3.41667H12.75M4.08333 3.41667V2.08333C4.08333 1.72971 4.22381 1.39057 4.47386 1.14052C4.72391 0.890476 5.06304 0.75 5.41667 0.75H8.08333C8.43696 0.75 8.77609 0.890476 9.02614 1.14052C9.27619 1.39057 9.41667 1.72971 9.41667 2.08333V3.41667"
                                          stroke="#FB2C36"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                      <span className="text-gray-900">Delete</span>
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ))}

          {/* Grid View */}
          {viewMode === "grid" &&
            projectResource.tag !== "loading" &&
            agenciesResource.tag !== "loading" &&
            workersResource.tag !== "loading" &&
            (currentProjects.length === 0 ? (
              <EmptyProjectsState />
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {currentProjects.map((project) => (
                  <div
                    key={project.id}
                    className="flex flex-col overflow-hidden rounded-lg border border-[#E2E8F0] bg-white p-6 transition-shadow hover:shadow-md"
                  >
                    <div className="flex-1">
                      <div className="mb-2 flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          {project.status && (
                            <StatusChangePopover
                              currentStatusId={
                                realProjects.find((rp) => rp.id === project.id)?.statusId || null
                              }
                              statuses={projectStatuses}
                              onStatusChange={(statusId) =>
                                handleStatusChange(project.id, statusId)
                              }
                            >
                              <button className="cursor-pointer transition-opacity hover:opacity-80">
                                <Badge
                                  style={{
                                    backgroundColor: project.statusColor,
                                    color: "white",
                                  }}
                                  className="border-0"
                                >
                                  {project.status}
                                </Badge>
                              </button>
                            </StatusChangePopover>
                          )}
                          <StatusBadge
                            label={project.type}
                            color={project.type === "Ongoing" ? "blue" : "orange"}
                            type="type1"
                            state="fill"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          {project.alert !== "-" && (
                            <div className="flex items-center gap-1 rounded-md border border-orange-400 bg-white px-2 py-1">
                              <svg
                                className="h-4 w-4 text-orange-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                />
                              </svg>
                              <span className="text-xs font-medium text-orange-600">
                                {project.alert}
                              </span>
                            </div>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-gray-100">
                                <svg
                                  className="h-4 w-4 text-gray-600"
                                  fill="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <circle cx="5" cy="12" r="2" />
                                  <circle cx="12" cy="12" r="2" />
                                  <circle cx="19" cy="12" r="2" />
                                </svg>
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-40">
                              <DropdownMenuItem
                                className="flex cursor-pointer items-center gap-2"
                                onClick={() => {
                                  navigate({
                                    to: "/dashboard/admin/projects/$projectId",
                                    params: { projectId: project.id },
                                    search: { edit: true },
                                  });
                                }}
                              >
                                <svg
                                  width="15"
                                  height="15"
                                  viewBox="0 0 15 15"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M8.08333 13.417H13.4167M9.41667 2.75033L12.0833 5.41699M13.5326 3.95825C13.8851 3.60587 14.0831 3.1279 14.0832 2.62949C14.0833 2.13108 13.8853 1.65306 13.533 1.30059C13.1806 0.948115 12.7026 0.750063 12.2042 0.75C11.7058 0.749937 11.2278 0.94787 10.8753 1.30025L1.97796 10.1996C1.82317 10.3539 1.7087 10.5439 1.64462 10.7529L0.763956 13.6543C0.746726 13.7119 0.745425 13.7732 0.76019 13.8315C0.774956 13.8898 0.805236 13.9431 0.84782 13.9856C0.890405 14.0281 0.943703 14.0583 1.00206 14.073C1.06042 14.0876 1.12166 14.0862 1.17929 14.0689L4.08129 13.1889C4.29007 13.1254 4.48007 13.0116 4.63462 12.8576L13.5326 3.95825Z"
                                    stroke="#020618"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                <span className="text-gray-900">Edit</span>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="flex cursor-pointer items-center gap-2"
                                onClick={() => handleDeleteProject(project.id)}
                                disabled={deleting}
                              >
                                <svg
                                  width="14"
                                  height="15"
                                  viewBox="0 0 14 15"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M5.41667 6.75V10.75M8.08333 6.75V10.75M11.4167 3.41667V12.75C11.4167 13.1036 11.2762 13.4428 11.0261 13.6928C10.7761 13.9429 10.437 14.0833 10.0833 14.0833H3.41667C3.06304 14.0833 2.72391 13.9429 2.47386 13.6928C2.22381 13.4428 2.08333 13.1036 2.08333 12.75V3.41667M0.75 3.41667H12.75M4.08333 3.41667V2.08333C4.08333 1.72971 4.22381 1.39057 4.47386 1.14052C4.72391 0.890476 5.06304 0.75 5.41667 0.75H8.08333C8.43696 0.75 8.77609 0.890476 9.02614 1.14052C9.27619 1.39057 9.41667 1.72971 9.41667 2.08333V3.41667"
                                    stroke="#FB2C36"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                <span className="text-gray-900">Delete</span>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>

                      <Link
                        to="/dashboard/admin/projects/$projectId"
                        params={{ projectId: project.id }}
                      >
                        <h3 className="mb-6 cursor-pointer text-2xl font-semibold text-gray-900 hover:text-gray-900 hover:underline">
                          {project.name}
                        </h3>
                      </Link>

                      <div className="space-y-4">
                        <div className="flex items-center gap-3">
                          <MapPin
                            className={`h-5 w-5 ${project.location && project.location !== "-" ? "text-gray-900" : "text-muted-foreground"}`}
                          />
                          <span className="text-sm text-gray-900">
                            {project.location && project.location !== "-" ? project.location : "-"}
                          </span>
                        </div>

                        <div className="flex items-center gap-3">
                          <User
                            className={`h-5 w-5 ${project.customer && project.customer !== "-" ? "text-gray-900" : "text-muted-foreground"}`}
                          />
                          <span className="text-sm text-gray-900">
                            {project.customer && project.customer !== "-" ? (
                              <Link
                                to="/dashboard/admin/customers/$customerId"
                                params={{ customerId: project.customerId }}
                                className="hover:text-gray-900 hover:underline"
                              >
                                {project.customer}
                              </Link>
                            ) : (
                              "-"
                            )}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Building2
                              className={`h-5 w-5 ${project.agency && project.agency !== "-" ? "text-gray-900" : "text-muted-foreground"}`}
                            />
                            <span className="text-sm text-gray-900">
                              {project.agency && project.agency !== "-" ? (
                                <Link
                                  to="/dashboard/admin/agencies/$agencyId"
                                  params={{ agencyId: project.agencyId }}
                                  className="hover:text-gray-900 hover:underline"
                                >
                                  {project.agency}
                                </Link>
                              ) : (
                                "-"
                              )}
                            </span>
                          </div>

                          <div className="flex items-center gap-2">
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className={
                                project.workers && project.workers !== "0/0"
                                  ? "text-gray-900"
                                  : "text-muted-foreground"
                              }
                            >
                              <path
                                d="M8.99967 5.3335H6.99967M9.99967 1.3335L9.33301 2.66683H11.333C11.6866 2.66683 12.0258 2.80731 12.2758 3.05735C12.5259 3.3074 12.6663 3.64654 12.6663 4.00016V13.3335C12.6663 13.6871 12.5259 14.0263 12.2758 14.2763C12.0258 14.5264 11.6866 14.6668 11.333 14.6668H4.66634C4.31272 14.6668 3.97358 14.5264 3.72353 14.2763C3.47348 14.0263 3.33301 13.6871 3.33301 13.3335V4.00016C3.33301 3.64654 3.47348 3.3074 3.72353 3.05735C3.97358 2.80731 4.31272 2.66683 4.66634 2.66683H6.66634M11.2657 14.6669C11.1123 13.9137 10.7034 13.2367 10.1082 12.7504C9.51294 12.2642 8.76794 11.9985 7.99932 11.9985C7.23071 11.9985 6.48571 12.2642 5.89047 12.7504C5.29523 13.2367 4.88632 13.9137 4.73299 14.6669M5.99967 1.3335L7.99967 5.3335M9.99967 10.0002C9.99967 11.1047 9.10424 12.0002 7.99967 12.0002C6.8951 12.0002 5.99967 11.1047 5.99967 10.0002C5.99967 8.89559 6.8951 8.00016 7.99967 8.00016C9.10424 8.00016 9.99967 8.89559 9.99967 10.0002Z"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <span className="text-sm text-gray-900">{project.workers}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="-mx-6 mt-6 -mb-6 border-t border-[#E2E8F0] bg-slate-50">
                      <div className="flex items-center justify-between px-6 py-3">
                        <span
                          className={`text-sm ${project.managers && project.managers !== "-" ? "text-gray-600" : "text-gray-400"}`}
                        >
                          {project.managers && project.managers !== "-"
                            ? project.managers
                            : "Managers not assigned"}
                        </span>
                        {(project.startDate || project.endDate) && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <svg
                              className="h-4 w-4 text-gray-500"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                            <span>
                              {project.startDate && project.endDate
                                ? `${project.startDate.toDate().toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: "America/New_York" })} - ${project.endDate.toDate().toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: "America/New_York" })}`
                                : project.startDate
                                  ? project.startDate.toDate().toLocaleDateString("en-US", {
                                      year: "numeric",
                                      month: "2-digit",
                                      day: "2-digit",
                                      timeZone: "America/New_York",
                                    })
                                  : project.endDate?.toDate().toLocaleDateString("en-US", {
                                      year: "numeric",
                                      month: "2-digit",
                                      day: "2-digit",
                                      timeZone: "America/New_York",
                                    })}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}

          {/* Map View */}
          {viewMode === "map" &&
            projectResource.tag !== "loading" &&
            agenciesResource.tag !== "loading" &&
            workersResource.tag !== "loading" && (
              <ProjectsMapView
                projects={realProjects.filter((p) => filteredProjects.some((fp) => fp.id === p.id))}
              />
            )}

          {/* Calendar View */}
          {viewMode === "calendar" &&
            projectResource.tag !== "loading" &&
            agenciesResource.tag !== "loading" &&
            workersResource.tag !== "loading" && (
              <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                <ProjectsCalendarView
                  projects={realProjects.filter((p) => {
                    // Apply the same filtering logic as for other views
                    const project = projects.find((proj) => proj.id === p.id);
                    return project && filteredProjects.some((fp) => fp.id === p.id);
                  })}
                  admins={users.filter((u) => u.type === "admin")}
                  managers={users.filter((u) => u.type === "manager")}
                  customers={users.filter((u) => u.type === "customer")}
                />
              </div>
            )}

          {/* Pagination - Show only when there are more than 200 items and in list view */}
          {showPagination && currentProjects.length > 0 && viewMode === "list" && (
            <div className="flex justify-center rounded-lg border border-gray-200 bg-white py-4">
              <Pagination>
                <PaginationContent>
                  <PaginationPrevious
                    onClick={() => {
                      setUrlState({ page: String(Math.max(1, currentPage - 1)) });
                    }}
                    disabled={currentPage === 1}
                  />

                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <PaginationItem key={page}>
                      <PaginationLink
                        isActive={currentPage === page}
                        onClick={() => setUrlState({ page: String(page) })}
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  ))}

                  {totalPages > 3 && <PaginationEllipsis />}

                  <PaginationNext
                    onClick={() => {
                      setUrlState({ page: String(Math.min(totalPages, currentPage + 1)) });
                    }}
                    disabled={currentPage === totalPages}
                  />
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
